import {
  CommonModule,
  Location,
  NgOptimizedImage,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  numberAttribute,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DateHelper } from '@app/date-helper';
import { DirectivesModule } from '@app/directives/directives.module';
import { HostelSearchParams, SearchService } from '@app/search.service';
import {
  AllAvailabilityResponse,
  ApiService,
  AvailabilityResponse,
  Hostel,
  Rooms,
} from '@app/services/api/api.service';
import { WhenMobileScrollComponent } from '@app/when-mobile-scroll/when-mobile-scroll.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { BedSelection, BookingService } from '../booking.service';
import { RoomListComponent } from '../room-list/room-list.component';
import { RoomSelectionFooterComponent } from '../room-selection-footer/room-selection-footer.component';

@Component({
  selector: 'app-hostel-rooms',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgOptimizedImage,
    AngularSvgIconModule,
    RoomListComponent,
    RoomSelectionFooterComponent,
    MatBottomSheetModule,
    WhenMobileScrollComponent,
    FormsModule,
    DirectivesModule,
  ],
  templateUrl: './hostel-rooms.component.html',
  styleUrls: ['./hostel-rooms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HostelRoomsComponent {
  @Input({ required: true })
  public id;
  @Input({ transform: numberAttribute, required: true })
  public noOfGuests = 1;
  @Input({ required: true })
  public checkInDate;
  @Input({ required: true })
  public checkOutDate;
  @Input({ required: true })
  public currency;

  @ViewChild('noOfGuestsInput', { static: false })
  public set noOfGuestsInput(el: ElementRef | undefined) {
    if (el) {
      this._noOfGuestsInput = el;
      this._noOfGuestsInput.nativeElement.focus();
    }
  }

  private _noOfGuestsInput: ElementRef | undefined;
  public hostel: Hostel;

  public selectedBeds: any[] = [];
  public total;
  public payableNow;
  public numNights = 1;
  public rooms: any;

  public unavailableDates: any;
  public noAvailability = false;
  private destroyed = new Subject<void>();
  public loading = true;
  public editingNoOfGuests = false;

  public bottomSheetOpen = false;

  constructor(
    private readonly bookingService: BookingService,
    private readonly searchService: SearchService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly apiService: ApiService,
    private readonly route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: string,
    public location: Location
  ) {}

  openBottomSheet(): void {
    this.bottomSheetOpen = true;
  }

  closeBottomSheet() {
    this.bottomSheetOpen = false;
  }

  toggleNoOfGuestsInput() {
    this.editingNoOfGuests = !this.editingNoOfGuests;
  }

  search() {
    this.searchService.setSearchParam('noOfGuests', this.noOfGuests);
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.route.data.subscribe((data) => {
        if (data['hostel']) {
          this.hostel = data['hostel'].data;
        }
      });

      this.route.queryParamMap
        .pipe(
          switchMap((paramsAsMap) => {
            this.bookingService.clearSelection();

            const checkInDate = paramsAsMap.get('checkInDate');
            const checkOutDate = paramsAsMap.get('checkOutDate');

            this.noOfGuests = +(paramsAsMap.get('noOfGuests') ?? 1);

            this.checkInDate = checkInDate;
            this.checkOutDate = checkOutDate;

            return this.apiService.getAvailability(this.id, {
              ...paramsAsMap,
              checkInDate,
              checkOutDate,
              currency: paramsAsMap.get('currency'),
            });
          }),
          takeUntil(this.destroyed)
        )
        .subscribe((resp: AllAvailabilityResponse) => {
          if (resp?.data?.date_wise_availability.length === 0) {
            this.noAvailability = true;
          } else {
            this.noAvailability = false;
          }
          this.unavailableDates = [
            ...new Set(
              (resp?.data?.date_wise_availability ?? [])
                .filter((d) => !d.available)
                .map((d) => {
                  return {
                    ...d,
                    date: DateHelper.normalizeDateToDateString(d.date),
                    // date: d.date.slice(0, 10)
                  };
                })
            ),
          ];
          this.cdr.markForCheck();
        });

      console.log('Checking availability', this.id, this.rooms);

      this.route.queryParamMap
        .pipe(
          switchMap((paramsAsMap) => {
            return this.apiService.checkAvailability(this.id, {
              ...paramsAsMap,
              currency: paramsAsMap.get('currency'),
            });
          }),
          takeUntil(this.destroyed)
        )
        .subscribe((resp: AvailabilityResponse) => {
          if ((resp as any).errorCode === 1302) {
            this.rooms = {} as unknown as Rooms;
          } else {
            this.rooms = resp.data?.rooms ?? ({} as Rooms);
          }
          this.cdr.markForCheck();
        });

      this.searchService.searchParams$
        .pipe(take(1))
        .subscribe((searchParams) => {
          this.numNights = this.searchService.calculateNights(
            searchParams['checkInDate'],
            searchParams['checkOutDate']
          );
        });

      this.bookingService.selectedBeds$
        .pipe(takeUntil(this.destroyed))
        .subscribe((beds) => {
          this.groupSelectedBeds(beds);
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (
      changes['noOfGuests'] ||
      changes['checkInDate'] ||
      changes['checkOutDate']
    ) {
      this.bookingService.clearSelection();

      // this should only happen after getavailability is resolved, because
      // the route may have to change anyway
      this.apiService
        .checkAvailability(this.id, {
          checkInDate: DateHelper.normalizeDateToDateString(this.checkInDate),
          checkOutDate: DateHelper.normalizeDateToDateString(this.checkOutDate),
          noOfGuests: this.noOfGuests,
          currency: this.currency,
        })
        .pipe(take(1))
        .subscribe((resp: AvailabilityResponse) => {
          if ((resp as any).errorCode === 1302) {
            this.rooms = {
              dormRooms: [],
              privateRooms: [],
              familyRooms: [],
            };
          } else {
            this.rooms = resp.data?.rooms ?? {
              dormRooms: [],
              privateRooms: [],
              familyRooms: [],
            };
          }
          // this.rooms = rooms; // hard coded to mock rooms
          this.cdr.markForCheck();
        });
    }

    if (changes['noOfGuests']) {
      const today = new Date();
      const sixMonthsFroMNow = new Date(today);
      sixMonthsFroMNow.setDate(sixMonthsFroMNow.getDate() + 182);

      // todo get first available day for # of guests (can include in seo route)
      this.apiService
        .getAvailability(this.id, {
          checkInDate: DateHelper.normalizeDateToDateString(today),
          checkOutDate: DateHelper.normalizeDateToDateString(sixMonthsFroMNow),
          noOfGuests: this.noOfGuests,
          currency: this.currency,
        })
        .pipe(take(1), takeUntil(this.destroyed))
        .subscribe((resp: AllAvailabilityResponse) => {
          if (resp?.data?.date_wise_availability.length === 0) {
            this.noAvailability = true;
          } else {
            this.noAvailability = false;
          }
          this.unavailableDates = [
            ...new Set(
              (resp?.data?.date_wise_availability ?? [])
                .filter((d) => !d.available)
                .map((d) => {
                  return {
                    ...d,
                    date: DateHelper.normalizeDateToDateString(d.date),
                    // date: d.date.slice(0, 10)
                  };
                })
            ),
          ];

          let moved = false;
          let start = DateHelper.normalizeDateToDateString(this.checkInDate);
          const s = new Date(start);
          // only need to do this if not some future date?
          for (let i = 0; i < this.unavailableDates.length; i++) {
            if (start === this.unavailableDates[i].date) {
              moved = true;
              // const s = new Date(start);
              s.setDate(s.getDate() + 1);
              start = DateHelper.normalizeDateToDateString(s);
            } else {
              break;
            }
          }

          if (moved) {
            // this might mess up normalization
            const end = s;
            end.setDate(s.getDate() + 1);
            this.router.navigate([], {
              queryParams: {
                checkInDate: start,
                checkOutDate: DateHelper.formatDate(end),
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          } else {
            this.cdr.markForCheck();
          }

          // find first date that is available, and use that as the start date (immediately route to it)
          // in this component, dont even bother with search service. more complicated than it is worth
        });
    }
  }

  private groupSelectedBeds(selectedBeds): void {
    this.selectedBeds = [];
    // THIS selected beds needs to be mapped over to get the rates
    selectedBeds.forEach((s: BedSelection, id: string) => {
      const rates = Object.keys(s.rates)
        .map((rateType) => ({
          rateType,
          count: s.rates[rateType] || 0,
        }))
        .filter((rate) => rate.count > 0);

      if (rates.length > 0) {
        this.selectedBeds.push({
          room: s.room,
          rates,
        });
      }
    });

    let totalPrice = 0;

    this.selectedBeds.forEach((s: any) => {
      const totalRoomPrice = s.rates.reduce((total, rate) => {
        return total + s.room.rates[rate.rateType].price * rate.count;
      }, 0);
      totalPrice += totalRoomPrice;
    });
    // need to be able to book multiple rooms
    this.total = totalPrice * this.numNights;

    this.cdr.markForCheck();
  }

  public goToBooking() {
    if (!this.selectedBeds.length) {
      return;
    }

    const payload: Rooms = this.selectedBeds.reduce(
      (prev: any, s: { room: any; rates: any }) => {
        let privateRooms = prev.privateRooms;
        let dormRooms = prev.dormRooms;
        let familyRooms = prev.familyRooms;
        const room = {
          privateRoomCategory: s.room.privateRoomCategory,
          roomType: s.room.roomType,
          name: s.room.name,
          _id: s.room._id,
        };

        // rateType: {
        //   standard: 1,
        //   nonRefundable: 2,
        // },

        if (s.room.roomType === 1) {
          s.rates.forEach((rate) => {
            for (let i = 0; i < rate.count; i++) {
              privateRooms.push({
                ...room,
                rateType: rate.rateType === 'standard' ? 1 : 2,
              });
            }
          });
        } else if (s.room.roomType === 2) {
          s.rates.forEach((rate) => {
            dormRooms.push({
              ...room,
              rateType: rate.rateType === 'standard' ? 1 : 2,
              noOfBookedBeds: rate.count,
            });
          });
        } else if (s.room.roomType === 3) {
          s.rates.forEach((rate) => {
            for (let i = 0; i < rate.count; i++) {
              familyRooms.push({
                ...room,
                rateType: rate.rateType === 'standard' ? 1 : 2,
              });
            }
          });
        }

        return {
          dormRooms,
          familyRooms,
          privateRooms,
        };
      },
      {
        dormRooms: [],
        familyRooms: [],
        privateRooms: [],
      }
    );

    this.bookingService.updateRooms(payload);

    this.searchService.searchParams$
      .pipe(
        take(1),
        switchMap((searchParams: HostelSearchParams) => {
          // const beds = this.encodeSelectedBeds(this.selectedBeds);

          localStorage.setItem(
            'selectedBeds',
            JSON.stringify(this.selectedBeds)
          );
          return this.apiService
            .lockBooking(this.id, {
              checkInDate: searchParams.checkInDate,
              checkOutDate: searchParams.checkOutDate,
              noOfGuests: searchParams.noOfGuests,
              // ...beds
              rooms: payload,
            })
            .pipe(
              tap((resp) => {
                this.bookingService.booking = resp.data;
                localStorage.setItem(`booking`, JSON.stringify(resp.data));
                this.router.navigate(['booking', resp.data._id]);
              })
            );
        })
      )
      .subscribe();
  }
}
